import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { authAxios } from "@/utils/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { FileText, Link as LinkIcon, Plus, Trash2, RefreshCw, ExternalLink, Eye, Download, Sparkles, Database, FileUser, MessageSquareText, Palette } from "lucide-react";
import { toast } from "sonner";
import { useLearningProfileStatus } from "@/hooks/useLearningProfileStatus";
import { useCoinGate } from "@/contexts/CoinGateContext";

export default function DataSourcesManager({ ownerType, ownerId, ownerName }) {
  const navigate = useNavigate();
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const { requireCoins } = useCoinGate();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [scraping, setScraping] = useState(false);
  const [maxSize, setMaxSize] = useState(0);
  const [selectedSources, setSelectedSources] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [regenerating, setRegenerating] = useState(null); // Track which source is regenerating
  const [generatingStyleCard, setGeneratingStyleCard] = useState(false);
  const [generatingLearningProfiles, setGeneratingLearningProfiles] = useState(false);
  const [showStyleCardOverwriteAlert, setShowStyleCardOverwriteAlert] = useState(false);
  const [showTokenLimitAlert, setShowTokenLimitAlert] = useState(false);
  const [showLearningTokenLimitAlert, setShowLearningTokenLimitAlert] = useState(false);
  const [progressToastId, setProgressToastId] = useState(null);
  const [learningProgressToastId, setLearningProgressToastId] = useState(null);

  // Server-side "LP generation in flight" state — polled every 2s via
  // GET /learning-profiles/status. Sole source of truth so the button
  // survives navigation and stays in sync across tabs/devices.
  // Replaces the old localStorage `learning_gen_cooldown_${ownerId}`
  // wall-clock timer (deleted 2026-07-12).
  const lpStatus = useLearningProfileStatus(
    ownerType === "relationship" ? "relationship" : "user",
    ownerId
  );
  const isLpGenerating = !!lpStatus.in_progress;
  const lpProgressLabel = isLpGenerating
    ? `Generating (${lpStatus.categories_done || 0}/${lpStatus.categories_total || 16})`
    : null;

  // 6-minute cooldown state for Style Card
  const STYLE_COOLDOWN_MS = 6 * 60 * 1000;
  const styleCooldownKey = `style_gen_cooldown_${ownerId}`;
  const [styleCooldownRemaining, setStyleCooldownRemaining] = useState(0);

  // Check Style Card cooldown on mount and set up interval
  useEffect(() => {
    const checkCooldown = () => {
      let active = false;
      const styleStored = localStorage.getItem(styleCooldownKey);
      if (styleStored) {
        const elapsed = Date.now() - parseInt(styleStored, 10);
        if (elapsed < STYLE_COOLDOWN_MS) {
          setStyleCooldownRemaining(Math.ceil((STYLE_COOLDOWN_MS - elapsed) / 1000));
          active = true;
        } else {
          localStorage.removeItem(styleCooldownKey);
          setStyleCooldownRemaining(0);
        }
      }
      return active;
    };

    checkCooldown();
    const interval = setInterval(() => {
      checkCooldown();
    }, 1000);

    return () => clearInterval(interval);
  }, [styleCooldownKey]);

  const startStyleCooldown = () => {
    localStorage.setItem(styleCooldownKey, Date.now().toString());
    setStyleCooldownRemaining(STYLE_COOLDOWN_MS / 1000);
  };

  const isStyleCoolingDown = styleCooldownRemaining > 0;
  const styleCooldownLabel = isStyleCoolingDown
    ? `Generating (${Math.floor(styleCooldownRemaining / 60)}:${String(styleCooldownRemaining % 60).padStart(2, '0')})`
    : null;
  
  // Form state
  const [formData, setFormData] = useState({
    title: "",
    data_source_url: "",
    content: "",
    forStyleAnalysis: false
  });

  useEffect(() => {
    fetchSources();
    fetchMaxSize();
  }, [ownerType, ownerId]);

  const fetchMaxSize = async () => {
    try {
      const response = await authAxios.get(`/admin/settings`);
      setMaxSize(response.data.data_source_max_size || 0);
    } catch (error) {
      console.error("Failed to fetch size limit:", error);
    }
  };

  const fetchSources = async () => {
    try {
      const response = await authAxios.get(`/data-sources`, {
        params: { owner_type: ownerType, owner_id: ownerId },
      });
      setSources(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data sources:", error);
      setLoading(false);
    }
  };

  const handleScrapeText = async () => {
    if (!formData.data_source_url.trim()) {
      toast.error("Please enter a URL to scrape");
      return;
    }
    
    setScraping(true);
    try {
      const response = await authAxios.post(`/data-sources/scrape-text`, {
        url: formData.data_source_url
      });
      
      // Populate content field with scraped text
      setFormData({ ...formData, content: response.data.text });
      toast.success("Text scraped successfully!");
    } catch (error) {
      console.error("Error scraping:", error);
      toast.error(error.response?.data?.detail || "Failed to scrape URL");
    } finally {
      setScraping(false);
    }
  };

  const handleAdd = async () => {
    if (!formData.title.trim()) {
      toast.error("Title is required");
      return;
    }

    if (!formData.content.trim()) {
      toast.error("Content is required");
      return;
    }

    // Close modal immediately to prevent double clicks
    setDialogOpen(false);
    const savedForm = { ...formData };
    setFormData({ title: "", data_source_url: "", content: "", forStyleAnalysis: false });

    try {
      await authAxios.post(`/data-sources`, {
        owner_type: ownerType,
        owner_id: ownerId,
        source_method: "pasted",
        title: savedForm.title,
        ref_url: savedForm.data_source_url || null,
        content: savedForm.content,
        data_type: savedForm.forStyleAnalysis ? "style_reference" : "pasted"
      });

      toast.success("Data source added!");
      fetchSources();
    } catch (error) {
      console.error("Error adding data source:", error);
      toast.error(error.response?.data?.detail || "Failed to add data source");
    }
  };

  const handleDelete = async (sourceId) => {
    if (!window.confirm("Delete this data source?")) return;

    try {
      await authAxios.delete(`/data-sources/${sourceId}`);
      toast.success("Data source deleted");
      fetchSources();
    } catch (error) {
      console.error("Error deleting source:", error);
      toast.error("Failed to delete data source");
    }
  };

  const handleReScrape = async (source) => {
    try {
      toast.info("Re-scraping URL...");
      await authAxios.post(`/data-sources/scrape`, {
        source_id: source.source_id,
        url: source.scrape_url
      });
      
      toast.success("URL re-scraped!");
      fetchSources();
    } catch (error) {
      console.error("Error re-scraping:", error);
      toast.error("Failed to re-scrape URL");
    }
  };

  const getCharCount = (text) => {
    return text ? text.length : 0;
  };

  const formatSize = (chars) => {
    if (chars < 1000) return `${chars} chars`;
    if (chars < 1000000) return `${(chars / 1000).toFixed(1)}K chars`;
    return `${(chars / 1000000).toFixed(1)}M chars`;
  };

  const formatWordCount = (words) => {
    if (!words) return null;
    if (words < 1000) return `${words} words`;
    return `${(words / 1000).toFixed(1)}K words`;
  };

  const formatTokenCount = (tokens) => {
    if (!tokens) return null;
    if (tokens < 1000) return `~${tokens} tokens`;
    return `~${(tokens / 1000).toFixed(1)}K tokens`;
  };

  const handleRegenerate = async (source) => {
    if (!(await requireCoins())) return;
    setRegenerating(source.source_id);
    try {
      // Use different endpoint for core sources
      const isCoreSouce = source.data_type === "core_compatibility" || source.data_type === "core_profile_deep";
      const endpoint = isCoreSouce 
        ? `/data-sources/${source.source_id}/regenerate-core`
        : `/data-sources/${source.source_id}/regenerate`;
      
      const response = await authAxios.post(endpoint);
      toast.success(`${source.title || "Data source"} regenerated!`);
      fetchSources();
    } catch (error) {
      console.error("Error regenerating:", error);
      toast.error(error.response?.data?.detail || "Failed to regenerate data source");
    } finally {
      setRegenerating(null);
    }
  };

  // Regenerate style card with relevance scoring
  const handleRegenerateStyleCard = async (source) => {
    setRegenerating(source.source_id);
    try {
      // Get reference sources (sources used to create this style card)
      let referenceIds = source.reference_source_ids || source.source_ids_used || [];
      
      // If no reference IDs, we need to get all available sources for this owner
      if (referenceIds.length === 0) {
        // Use currently available sources (excluding the style card itself)
        referenceIds = sources
          .filter(s => s.source_id !== source.source_id && 
                       s.data_type !== "match_style_card" && 
                       s.data_type !== "user_style_card")
          .map(s => s.source_id);
      }
      
      if (referenceIds.length === 0) {
        toast.error("No source data available to regenerate style card. Add data sources first.");
        return;
      }
      if (!(await requireCoins())) return;

      toast.info("Regenerating style card...");
      
      const response = await authAxios.post(
        `/data-sources/generate-style-card-full/${ownerId}`,
        { source_ids: referenceIds },
        { timeout: 180000 }
      );
      
      toast.success("Style card regenerated with updated relevance scores!");
      fetchSources();
    } catch (error) {
      console.error("Error regenerating style card:", error);
      if (error.code === 'ECONNABORTED') {
        toast.info("Style card generation in progress. This may take a moment...");
        // Poll for completion
        setTimeout(() => fetchSources(), 5000);
      } else {
        // Handle validation errors that come as objects
        const detail = error.response?.data?.detail;
        const errorMessage = typeof detail === 'string' 
          ? detail 
          : (Array.isArray(detail) ? detail[0]?.msg : "Failed to regenerate style card");
        toast.error(errorMessage);
      }
    } finally {
      setRegenerating(null);
    }
  };

  // Get icon for data source type
  const getSourceIcon = (source) => {
    if (source.data_type === "match_style_card" || source.data_type === "user_style_card") {
      return <Palette className="w-5 h-5 mt-1 text-green-400" />;
    }
    if (source.data_type === "style_reference") {
      return <MessageSquareText className="w-5 h-5 mt-1 text-pink-400" />;
    }
    if (source.is_core_dataset || source.data_type === "core_dataset") {
      return <Database className="w-5 h-5 mt-1 text-purple-400" />;
    }
    if (source.is_core_profile || source.data_type === "core_profile") {
      return <FileUser className="w-5 h-5 mt-1 text-emerald-400" />;
    }
    if (source.auto_imported) {
      return <FileText className="w-5 h-5 mt-1 text-cyan-400" />;
    }
    if (source.source_method === "url_scraped") {
      return <LinkIcon className="w-5 h-5 mt-1 text-cyan-400" />;
    }
    return <FileText className="w-5 h-5 mt-1 text-divine-gold" />;
  };

  // Get badge for data source type
  const getSourceBadge = (source) => {
    if (source.data_type === "match_style_card" || source.data_type === "user_style_card") {
      return (
        <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full border border-green-500/30 font-medium">
          Style Card
        </span>
      );
    }
    if (source.data_type === "style_reference") {
      return (
        <span className="px-2 py-0.5 bg-pink-500/20 text-pink-400 text-xs rounded-full border border-pink-500/30 font-medium">
          Style Analysis
        </span>
      );
    }
    if (source.is_core_dataset || source.data_type === "core_dataset") {
      return (
        <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded-full border border-purple-500/30 font-medium">
          LLM Memory
        </span>
      );
    }
    if (source.is_core_profile || source.data_type === "core_profile") {
      return (
        <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full border border-emerald-500/30 font-medium">
          Human Copy
        </span>
      );
    }
    if (source.auto_imported) {
      return (
        <span className="px-2 py-0.5 bg-cyan-500/20 text-cyan-400 text-xs rounded-full border border-cyan-500/30">
          Auto
        </span>
      );
    }
    return null;
  };

  // Selection handlers
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedSources(sources.map(s => s.source_id));
    } else {
      setSelectedSources([]);
    }
  };

  const handleSelectSource = (sourceId, checked) => {
    if (checked) {
      setSelectedSources([...selectedSources, sourceId]);
    } else {
      setSelectedSources(selectedSources.filter(id => id !== sourceId));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedSources.length === 0) {
      toast.error("No sources selected");
      return;
    }
    
    // Filter out auto-imported sources
    const deletableSources = sources.filter(
      s => selectedSources.includes(s.source_id) && !s.auto_imported
    );
    
    if (deletableSources.length === 0) {
      toast.error("Cannot delete auto-imported sources");
      return;
    }
    
    if (!window.confirm(`Delete ${deletableSources.length} selected data source(s)?`)) return;

    try {
      for (const source of deletableSources) {
        await authAxios.delete(`/data-sources/${source.source_id}`);
      }
      toast.success(`Deleted ${deletableSources.length} data source(s)`);
      setSelectedSources([]);
      fetchSources();
    } catch (error) {
      console.error("Error deleting sources:", error);
      toast.error("Failed to delete some data sources");
    }
  };

  const handleGenerateLearningProfiles = async () => {
    if (selectedSources.length === 0) {
      toast.error("Please select at least one data source");
      return;
    }

    if (isLpGenerating) {
      toast.info("Generation already in progress. Please wait for it to complete.");
      return;
    }

    // Check token limit first (before generation)
    const totalTokens = getSelectedTokenTotal();
    if (totalTokens > 14000) {
      setShowLearningTokenLimitAlert(true);
      return;
    }

    if (!(await requireCoins())) return;

    setGeneratingLearningProfiles(true);

    // Pre-check: ensure category_relevance exists for selected sources
    try {
      const relevanceRes = await authAxios.post(
        `/data-sources/ensure-relevance`,
        { source_ids: selectedSources },
        { timeout: 120000 }
      );
      const { scored } = relevanceRes.data;
      if (scored > 0) {
        toast.info(`Scored relevance for ${scored} source(s) before generating.`);
      }
    } catch (error) {
      console.warn("Relevance pre-check failed, continuing with generation:", error);
    }

    // All 16 learning profile categories
    const allCategories = [
      "core_identity",
      "communication_style", 
      "love_language",
      "values_beliefs",
      "attractions_desires",
      "dislikes_boundaries",
      "lifestyle_interests",
      "social_dynamics",
      "emotional_patterns",
      "professional_work",
      "aesthetic_expression",
      "cultural_background",
      "skills_aptitudes",
      "relationship_ecosystem",
      "physical_mental_wellbeing",
      "leadership_orientation"
    ];

    try {
      // Kick off the actual generation and simultaneously refresh the
      // status poller so the button flips to "Generating (0/16)" within
      // ~1s instead of waiting for the next 2s tick.
      lpStatus.refresh?.();
      const response = await authAxios.post(
        `/learning-profiles/generate-batch`,
        {
          owner_type: ownerType,
          owner_id: ownerId,
          profile_categories: allCategories,
          source_ids: selectedSources  // Only send selected sources
        },
        { timeout: 300000 } // 5 minute timeout for 16 LLM calls
      );

      // Handle different response scenarios
      const { generated, synthesized, skipped_unchanged, sources_processed, message } = response.data;
      
      if (generated === 0 && skipped_unchanged > 0) {
        // All sources were unchanged
        toast.info("All sources are up to date. No regeneration needed.");
      } else if (synthesized > 0) {
        // Some profiles were synthesized with new data
        toast.success(`Updated ${generated} profiles (${synthesized} synthesized with new data)`);
      } else {
        // Fresh generation
        toast.success(`Generated ${generated} learning profile categories!`);
      }
      
      setGeneratingLearningProfiles(false);
      
      // Navigate to the learning profiles page
      if (ownerType === "relationship") {
        navigate(`/relationships/${ownerId}`, { state: { activeTab: 'learning' } });
      } else {
        navigate("/profile", { state: { activeTab: 'learning' } });
      }
    } catch (error) {
      console.error("Error generating learning profiles:", error);
      console.log("Error details - status:", error.response?.status, "code:", error.code, "message:", error.message);

      // 409 CONFLICT — another tab/device already fired a generate for
      // this owner. Server-side Redis lock is the source of truth; poke
      // the poller so the button reflects the in-flight job.
      if (error.response?.status === 409) {
        setGeneratingLearningProfiles(false);
        toast.info("A Learning Profile generation is already in progress. Please wait for it to complete.");
        lpStatus.refresh?.();
        return;
      }

      // Check if it's a network/gateway timeout (proxy cuts connection before backend completes)
      const isNetworkError = !error.response || error.code === 'ERR_NETWORK' || error.message?.includes('Network Error');
      const isGatewayTimeout = error.response?.status >= 500 && error.response?.status <= 599;
      const isTimeout = error.code === 'ECONNABORTED';
      
      if (isNetworkError || isGatewayTimeout || isTimeout) {
        // The request may still be processing on the backend - keep indicator running
        const toastId = toast.info("Generation in progress... This may take 2-3 minutes for high token analysis. Please wait...", {
          duration: 180000
        });
        setLearningProgressToastId(toastId);
        
        // Keep checking for completion - 12 attempts x 15s = 180s (3 minutes)
        const checkCompletion = async (attempt = 1) => {
          console.log(`Learning profiles check attempt ${attempt}/12`);
          if (attempt > 12) {
            toast.dismiss(toastId);
            setLearningProgressToastId(null);
            setGeneratingLearningProfiles(false);
            toast.warning("Generation is taking longer than expected. Check the Learning tab for results.");
            return;
          }
          
          // Check if profiles were created recently
          try {
            const profilesRes = await authAxios.get(`/learning-profiles`, {
              params: { owner_type: ownerType, owner_id: ownerId }
            });
            
            // Check if any profiles were updated in the last 5 minutes
            const recentProfile = profilesRes.data.find(p => 
              new Date(p.generated_at) > new Date(Date.now() - 5 * 60 * 1000)
            );
            
            if (recentProfile) {
              toast.dismiss(toastId);
              setLearningProgressToastId(null);
              setGeneratingLearningProfiles(false);
              toast.success(`Learning profiles generated successfully!`);
              // Navigate to learning tab
              if (ownerType === "relationship") {
                navigate(`/relationships/${ownerId}`, { state: { activeTab: 'learning' } });
              }
              return;
            }
            console.log(`No recent learning profiles found, will retry in 15s`);
          } catch (e) {
            console.log("Check attempt", attempt, "failed:", e);
          }
          
          // Try again in 15 seconds
          setTimeout(() => checkCompletion(attempt + 1), 15000);
        };
        
        // Start checking after 30 seconds (give backend time to process)
        setTimeout(() => checkCompletion(1), 30000);
      } else {
        setGeneratingLearningProfiles(false);
        const errorMsg = error.response?.data?.detail || "Failed to generate learning profiles";
        toast.error(errorMsg);
      }
    }
  };

  const [generatingCoreDataset, setGeneratingCoreDataset] = useState(false);

  const handleGenerateCoreDataset = async () => {
    if (ownerType !== "relationship") {
      toast.error("Core Data Set generation is only available for personas");
      return;
    }

    if (!(await requireCoins())) return;

    setGeneratingCoreDataset(true);
    try {
      const response = await authAxios.post(
        `/data-sources/generate-core-dataset/${ownerId}`
      );

      const { match_data, profile_deep, compatibility } = response.data;
      const dataType = match_data?.type === "user_data" ? "CORE_USER_DATA" : "CORE_MATCH_DATA";

      // Build toast message with all generated files
      let toastParts = [
        `${dataType} (${match_data?.word_count || 0} words)`
      ];
      
      if (profile_deep) {
        const deepType = match_data?.type === "user_data" ? "USER_PROFILE_DEEP" : "MATCH_PROFILE_DEEP";
        toastParts.push(`${deepType} (${profile_deep.word_count} words)`);
      }
      
      if (compatibility) {
        toastParts.push(`CORE_COMPATIBILITY (${compatibility.word_count} words)`);
      }
      
      toast.success(`Generated: ${toastParts.join(" + ")}`);
      fetchSources(); // Refresh the sources list
    } catch (error) {
      console.error("Error generating Core Data Set:", error);
      toast.error(error.response?.data?.detail || "Failed to generate Core Data Set");
    } finally {
      setGeneratingCoreDataset(false);
    }
  };

  // Check if any selected sources are style analysis or communication samples
  const hasStyleAnalysisSources = () => {
    return sources.some(source => 
      selectedSources.includes(source.source_id) && 
      (source.data_type === "style_reference" || 
       source.data_type === "communication_sample" ||
       source.title?.toLowerCase().includes("style") ||
       source.title?.toLowerCase().includes("chat") ||
       source.title?.toLowerCase().includes("communication"))
    );
  };

  // Check if existing STYLE_CARD exists but is NOT selected
  const getUnselectedStyleCard = () => {
    return sources.find(source => 
      !selectedSources.includes(source.source_id) &&
      (source.data_type === "match_style_card" || 
       source.data_type === "user_style_card" ||
       source.title === "MATCH_STYLE_CARD" ||
       source.title === "USER_STYLE_CARD")
    );
  };

  // Calculate total tokens from selected sources
  const getSelectedTokenTotal = () => {
    return sources
      .filter(source => selectedSources.includes(source.source_id))
      .reduce((total, source) => total + (source.token_count || 0), 0);
  };

  const handleGenerateStyleCard = async (forceOverwrite = false) => {
    if (selectedSources.length === 0) {
      toast.error("Please select at least one data source");
      return;
    }

    // Check token limit first (before overwrite check)
    const totalTokens = getSelectedTokenTotal();
    if (totalTokens > 9000) {
      setShowTokenLimitAlert(true);
      return;
    }

    // Check for existing style card that's not selected
    const unselectedStyleCard = getUnselectedStyleCard();
    if (unselectedStyleCard && !forceOverwrite) {
      setShowStyleCardOverwriteAlert(true);
      return;
    }

    if (!(await requireCoins())) return;
    setGeneratingStyleCard(true);
    startStyleCooldown();
    try {
      const response = await authAxios.post(
        `/data-sources/generate-style-card-full/${ownerId}`,
        { source_ids: selectedSources },
        { timeout: 300000 } // 5 minute timeout for LLM generation (Claude Opus can take 2-3 min)
      );

      toast.success(
        `${response.data.title} generated: ${response.data.word_count} words (~${response.data.token_count} tokens)`
      );
      setGeneratingStyleCard(false);
      setShowStyleCardOverwriteAlert(false);
      fetchSources(); // Refresh the sources list
    } catch (error) {
      console.error("Error generating style card:", error);
      console.log("Error details - status:", error.response?.status, "code:", error.code, "message:", error.message);
      
      // Check if it's a network/gateway timeout (proxy cuts connection before backend completes)
      // Kubernetes ingress typically times out at 60 seconds and may return various status codes
      const isNetworkError = !error.response || error.code === 'ERR_NETWORK' || error.message?.includes('Network Error');
      const isGatewayTimeout = error.response?.status >= 500 && error.response?.status <= 599; // Any 5xx error
      const isTimeout = error.code === 'ECONNABORTED';
      
      if (isNetworkError || isGatewayTimeout || isTimeout) {
        // The request may still be processing on the backend - keep indicator running
        const toastId = toast.info("Generation in progress... This may take 2-3 minutes for high token analysis. Please wait...", {
          duration: 180000
        });
        setProgressToastId(toastId);
        
        // Keep checking for completion - 12 attempts x 15s = 180s (3 minutes)
        const checkCompletion = async (attempt = 1) => {
          console.log(`Style card check attempt ${attempt}/12`);
          if (attempt > 12) {
            toast.dismiss(toastId);
            setProgressToastId(null);
            setGeneratingStyleCard(false);
            setShowStyleCardOverwriteAlert(false);
            toast.warning("Generation is taking longer than expected. Refreshing list...");
            fetchSources();
            return;
          }
          
          // Refresh sources to check if style card appeared
          try {
            const sourcesRes = await authAxios.get(`/data-sources?owner_type=relationship&owner_id=${ownerId}`);
            
            // Check if a new style card was created recently (within last 5 minutes)
            const recentStyleCard = sourcesRes.data.find(s => 
              (s.data_type === "match_style_card" || s.data_type === "user_style_card") &&
              new Date(s.regenerated_at) > new Date(Date.now() - 5 * 60 * 1000)
            );
            
            if (recentStyleCard) {
              toast.dismiss(toastId);
              setProgressToastId(null);
              setGeneratingStyleCard(false);
              setShowStyleCardOverwriteAlert(false);
              toast.success(`Style card generated successfully!`);
              fetchSources();
              return;
            }
            console.log(`No recent style card found, will retry in 15s`);
          } catch (e) {
            console.log("Check attempt", attempt, "failed:", e);
          }
          
          // Try again in 15 seconds
          setTimeout(() => checkCompletion(attempt + 1), 15000);
        };
        
        // Start checking after 30 seconds (give backend time to process)
        setTimeout(() => checkCompletion(1), 30000);
      } else {
        setGeneratingStyleCard(false);
        setShowStyleCardOverwriteAlert(false);
        const errorMsg = error.response?.data?.detail || "Failed to generate style card";
        toast.error(errorMsg);
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Style Card Overwrite Alert */}
      <AlertDialog open={showStyleCardOverwriteAlert} onOpenChange={setShowStyleCardOverwriteAlert}>
        <AlertDialogContent className="bg-divine-charcoal border-divine-gold/30">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-divine-gold font-cinzel">Warning: Overwrite Style Card</AlertDialogTitle>
            <AlertDialogDescription className="text-white/70">
              This will overwrite the existing STYLE_CARD.
              <br /><br />
              <span className="text-cyan-400">Tip:</span> Include the existing STYLE_CARD in your selection to merge communication style data into a new unified style card.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/20 text-white hover:bg-white/10">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleGenerateStyleCard(true)}
              className="bg-pink-600 hover:bg-pink-500 text-white"
            >
              Overwrite
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Token Limit Alert - Style Card */}
      <AlertDialog open={showTokenLimitAlert} onOpenChange={setShowTokenLimitAlert}>
        <AlertDialogContent className="bg-divine-charcoal border-red-500/30">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400 font-cinzel">Token Limit Exceeded</AlertDialogTitle>
            <AlertDialogDescription className="text-white/70">
              <p className="mb-3">
                Selected sources total <span className="text-red-400 font-bold">{getSelectedTokenTotal().toLocaleString()}</span> tokens, which exceeds the 9,000 token processing limit.
              </p>
              <p className="text-cyan-400">
                Reduce Token Processing Amount to less than 9k tokens. Select fewer files to process, or break up large communication log files into smaller files to batch process.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction 
              onClick={() => setShowTokenLimitAlert(false)}
              className="bg-divine-gold/80 hover:bg-divine-gold text-black"
            >
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Token Limit Alert - Learning Profiles */}
      <AlertDialog open={showLearningTokenLimitAlert} onOpenChange={setShowLearningTokenLimitAlert}>
        <AlertDialogContent className="bg-divine-charcoal border-red-500/30">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-400 font-cinzel">Token Limit Exceeded</AlertDialogTitle>
            <AlertDialogDescription className="text-white/70">
              <p className="mb-3">
                Selected sources total <span className="text-red-400 font-bold">{getSelectedTokenTotal().toLocaleString()}</span> tokens, which exceeds the 14,000 token processing limit.
              </p>
              <p className="text-cyan-400">
                Reduce Token Processing Amount to less than 14k tokens. Select fewer files to process, or break up large communication log files into smaller files to batch process.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction 
              onClick={() => setShowLearningTokenLimitAlert(false)}
              className="bg-divine-gold/80 hover:bg-divine-gold text-black"
            >
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-cinzel text-xl text-white">
            {ownerType === "user" ? "My Data Sources" : `${ownerName}'s Data Sources`}
          </h3>
          <p className="text-white/60 text-sm mt-1">
            Add information to help Agent Eros understand {ownerType === "user" ? "you" : "them"} better
          </p>
        </div>
        
        {/* Desktop Buttons */}
        <div className="hidden md:flex items-center gap-2">
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-divine-gold hover:bg-divine-gold-light text-black font-cinzel">
                <Plus className="w-4 h-4 mr-2" />
                Add New
              </Button>
            </DialogTrigger>
          
          <DialogContent className="bg-divine-charcoal border-divine-gold/30 text-white max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-cinzel text-xl text-divine-gold">
                Add Data Source
              </DialogTitle>
            </DialogHeader>

            {/* Unified Form (No Tabs) */}
            <div className="space-y-4 mt-4">
              {/* Style Analysis Toggle - At Top */}
              <div className={`flex items-center justify-between px-4 py-3 rounded-lg border ${
                formData.forStyleAnalysis 
                  ? "bg-pink-500/10 border-pink-500/30" 
                  : "bg-white/5 border-divine-gold/20"
              }`}>
                <div className="flex items-center gap-3">
                  <MessageSquareText className={`w-5 h-5 ${formData.forStyleAnalysis ? "text-pink-400" : "text-white/40"}`} />
                  <Label htmlFor="style-analysis-toggle" className={`text-sm cursor-pointer ${formData.forStyleAnalysis ? "text-pink-300" : "text-white/80"}`}>
                    Use this data to only Analyze {ownerName}'s Communication Style
                  </Label>
                </div>
                <Switch
                  id="style-analysis-toggle"
                  checked={formData.forStyleAnalysis}
                  onCheckedChange={(checked) => setFormData({ ...formData, forStyleAnalysis: checked })}
                  className="data-[state=checked]:bg-pink-500"
                />
              </div>

              <div>
                <Label className="text-white/80">Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder={formData.forStyleAnalysis 
                    ? "WhatsApp Chat history sample, Chat Logs, Emails, Written copy etc.." 
                    : "e.g., Instagram Bio, Personal Blog, Notes"
                  }
                  className="mt-2 bg-divine-black border-divine-gold/30 text-white"
                />
              </div>

              <div>
                <Label className="text-white/80">Data Source URL (Optional)</Label>
                <div className="flex gap-2 mt-2">
                  <Input
                    value={formData.data_source_url}
                    onChange={(e) => setFormData({ ...formData, data_source_url: e.target.value })}
                    placeholder="https://example.com/profile"
                    className="flex-1 bg-divine-black border-divine-gold/30 text-white"
                  />
                  {formData.data_source_url && (
                    <Button
                      onClick={handleScrapeText}
                      disabled={scraping}
                      className="bg-cyan-500 hover:bg-cyan-600 text-white"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {scraping ? "Scraping..." : "Scrape Text"}
                    </Button>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-white/80">Content *</Label>
                <Textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder={formData.forStyleAnalysis 
                    ? `For best Results, Paste Chat Logs, Emails, reflective writings, blogs and transcripts of any audio communication between ${ownerName} and yourself. This Data is used to create the style of communication coming from ${ownerName} to make any generated text actually sound like them.` 
                    : `Paste Any information about ${ownerName} here, or use the Data Source URL (above) to gather PUBLIC DATA ONLY from websites, bios, etc. (it will not be able to access private profiles).\n\nFOR PRIVATE PROFILES You must have access to them. You can COPY and PASTE the private profile data here from Social Media Bios, Dating Apps etc.`
                  }
                  className="mt-2 bg-divine-black border-divine-gold/30 text-white min-h-[250px]"
                />
                <div className="flex justify-between items-center mt-1">
                  <p className="text-xs text-white/40">
                    {getCharCount(formData.content).toLocaleString()} characters
                  </p>
                  {maxSize > 0 && (
                    <p className={`text-xs ${getCharCount(formData.content) > maxSize ? 'text-red-400' : 'text-white/40'}`}>
                      Limit: {maxSize.toLocaleString()}
                    </p>
                  )}
                </div>
              </div>
            </div>

            <Button
              onClick={handleAdd}
              className="w-full mt-4 bg-divine-gold hover:bg-divine-gold-light text-black font-cinzel"
            >
              Save Data Source
            </Button>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Mobile Buttons - Above Select All, vertically centered */}
      <div className="md:hidden flex flex-col items-center gap-2">
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="w-full max-w-xs bg-divine-gold hover:bg-divine-gold-light text-black font-cinzel">
              <Plus className="w-4 h-4 mr-2" />
              Add New
            </Button>
          </DialogTrigger>
        </Dialog>
        
      </div>

      {/* Rest of component... */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin w-8 h-8 border-2 border-divine-gold border-t-transparent rounded-full" />
        </div>
      ) : sources.length > 0 ? (
        <div className="space-y-3">
          {/* Select All Header */}
          <div className="flex items-center justify-between p-3 bg-white/5 rounded-lg border border-divine-gold/20">
            <div className="flex items-center gap-3">
              <Checkbox
                checked={selectedSources.length === sources.length && sources.length > 0}
                onCheckedChange={handleSelectAll}
                className="border-divine-gold/50"
              />
              <span className="text-white/80 text-sm">
                Select All ({selectedSources.length} of {sources.length} selected)
              </span>
            </div>
            {(selectedSources.length > 0 || isLpGenerating) && (
              <div className="flex items-center gap-2 flex-wrap">
                {/* Generate Communication Style-Card - only shown when style analysis/communication samples are selected */}
                {selectedSources.length > 0 && hasStyleAnalysisSources() && (
                  <Button
                    data-testid="generate-style-card-btn"
                    size="sm"
                    onClick={() => handleGenerateStyleCard(false)}
                    disabled={generatingStyleCard || isStyleCoolingDown}
                    className="bg-pink-600 hover:bg-pink-500 text-white disabled:opacity-60"
                  >
                    {generatingStyleCard || isStyleCoolingDown ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        {styleCooldownLabel || "Generating..."}
                      </>
                    ) : (
                      <>
                        <Palette className="w-4 h-4 mr-2" />
                        Generate Communication Style-Card
                      </>
                    )}
                  </Button>
                )}
                <Button
                  data-testid="generate-learning-profiles-btn"
                  size="sm"
                  onClick={handleGenerateLearningProfiles}
                  disabled={generatingLearningProfiles || isLpGenerating || selectedSources.length === 0}
                  className="bg-purple-600 hover:bg-purple-500 text-white disabled:opacity-60"
                  title={
                    isLpGenerating && selectedSources.length === 0
                      ? "A Learning Profile generation is already in progress for this match."
                      : undefined
                  }
                >
                  {generatingLearningProfiles || isLpGenerating ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      {lpProgressLabel || "Generating..."}
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      Generate Learning Profiles
                    </>
                  )}
                </Button>
                {selectedSources.length > 0 && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleDeleteSelected}
                    className="text-red-400 hover:bg-red-400/10"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Selected
                  </Button>
                )}
              </div>
            )}
          </div>

          {sources.map((source) => (
            <div
              key={source.source_id}
              className={`rounded-lg p-4 flex items-start justify-between ${
                source.data_type === "match_style_card" || source.data_type === "user_style_card"
                  ? "bg-green-500/5 border border-green-500/30"
                  : source.data_type === "style_reference"
                    ? "bg-pink-500/5 border border-pink-500/30"
                    : source.is_core_dataset || source.data_type === "core_dataset"
                      ? "bg-purple-500/5 border border-purple-500/30"
                      : source.is_core_profile || source.data_type === "core_profile"
                        ? "bg-emerald-500/5 border border-emerald-500/30"
                        : "glass-gold"
              }`}
            >
              <div className="flex items-start gap-3 flex-1">
                {/* Checkbox */}
                <Checkbox
                  checked={selectedSources.includes(source.source_id)}
                  onCheckedChange={(checked) => handleSelectSource(source.source_id, checked)}
                  className="border-divine-gold/50 mt-1"
                />
                {getSourceIcon(source)}
                <div className="flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className={`font-medium ${
                      source.data_type === "match_style_card" || source.data_type === "user_style_card"
                        ? "text-green-300"
                        : source.data_type === "style_reference"
                          ? "text-pink-300"
                          : source.is_core_dataset || source.data_type === "core_dataset"
                            ? "text-purple-300"
                            : source.is_core_profile || source.data_type === "core_profile"
                              ? "text-emerald-300"
                              : "text-white"
                    }`}>{source.title}</h4>
                    {getSourceBadge(source)}
                  </div>
                  {source.description && (
                    <p className="text-white/50 text-xs mt-1">{source.description}</p>
                  )}
                  {source.ref_url && (
                    <a
                      href={source.ref_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-cyan-400 text-sm hover:underline flex items-center gap-1 mt-1"
                    >
                      <ExternalLink className="w-3 h-3" />
                      {source.ref_url}
                    </a>
                  )}
                  {source.scrape_url && (
                    <a
                      href={source.scrape_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-cyan-400 text-sm hover:underline flex items-center gap-1 mt-1"
                    >
                      <ExternalLink className="w-3 h-3" />
                      {source.scrape_url}
                    </a>
                  )}
                  <div className="flex items-center gap-3 mt-2 text-xs text-white/40 flex-wrap">
                    {source.word_count && (
                      <span className="text-white/60">{formatWordCount(source.word_count)}</span>
                    )}
                    {source.token_count && (
                      <span className={`${
                        source.is_core_dataset || source.data_type === "core_dataset"
                          ? "text-purple-400"
                          : source.is_core_profile || source.data_type === "core_profile"
                            ? "text-emerald-400"
                            : "text-divine-gold/70"
                      }`}>{formatTokenCount(source.token_count)}</span>
                    )}
                    {!source.word_count && (
                      <span>{formatSize(source.char_count)}</span>
                    )}
                    <span>•</span>
                    <span>
                      {source.regenerated_at
                        ? `Regenerated ${new Date(source.regenerated_at).toLocaleDateString()}`
                        : source.source_method === "url_scraped" && source.last_scraped_at
                          ? `Scraped ${new Date(source.last_scraped_at).toLocaleDateString()}`
                          : `Added ${new Date(source.created_at).toLocaleDateString()}`}
                    </span>
                    {source.scrape_status === "failed" && (
                      <>
                        <span>•</span>
                        <span className="text-red-400">Scrape failed</span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-1">
                {/* View Button */}
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => navigate(`/data-source/${source.source_id}`)}
                  className="text-divine-gold hover:bg-divine-gold/10"
                  title="View"
                >
                  <Eye className="w-4 h-4" />
                </Button>
                
                {/* Regenerate Button for STYLE_CARD sources */}
                {(source.data_type === "match_style_card" || source.data_type === "user_style_card") && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRegenerateStyleCard(source)}
                    disabled={regenerating === source.source_id}
                    className="text-green-400 hover:bg-green-400/10"
                    title="Regenerate style card"
                  >
                    <RefreshCw className={`w-4 h-4 ${regenerating === source.source_id ? 'animate-spin' : ''}`} />
                  </Button>
                )}
                
                {/* Regenerate Button for auto-imported sources (except STYLE_CARD) */}
                {source.auto_imported && 
                 source.data_type !== "match_style_card" && 
                 source.data_type !== "user_style_card" && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRegenerate(source)}
                    disabled={regenerating === source.source_id}
                    className={`hover:bg-white/10 ${
                      source.data_type === "core_compatibility"
                        ? "text-pink-400"
                        : source.data_type === "core_profile_deep"
                          ? "text-amber-400"
                          : source.is_core_dataset || source.data_type === "core_dataset"
                            ? "text-purple-400"
                            : source.is_core_profile || source.data_type === "core_profile"
                              ? "text-emerald-400"
                              : "text-cyan-400"
                    }`}
                    title={`Regenerate ${source.title || "source"}`}
                  >
                    <RefreshCw className={`w-4 h-4 ${regenerating === source.source_id ? 'animate-spin' : ''}`} />
                  </Button>
                )}
                
                {/* Re-scrape Button for URL sources */}
                {source.source_method === "url_scraped" && !source.auto_imported && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleReScrape(source)}
                    className="text-cyan-400 hover:bg-cyan-400/10"
                    title="Re-scrape URL"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                )}
                
                {/* Delete Button for non-auto sources */}
                {!source.auto_imported && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(source.source_id)}
                    className="text-red-400 hover:bg-red-400/10"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 glass rounded-xl">
          <FileText className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <p className="text-white/60">
            No data sources yet. Add information to help Agent Eros learn.
          </p>
        </div>
      )}
    </div>
  );
}
